import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SiteConfigService } from '../core/site-config.service';
import { ResponsiveImageComponent } from '../shared/responsive-image.component';
import { FavoriteCollectionService, UserCollection } from '../widgets/collections/favorite-collection.service';

@Component({
  selector: 'app-collection-page',
  imports: [ResponsiveImageComponent, RouterLink],
  template: `
    <section class="shared-collection">
      @if (loading()) {
        <div class="collection-state">{{ i18n.t('comingSoon') }}</div>
      } @else if (collection(); as item) {
        <header><p>{{ i18n.collectionT('share') }}</p><h1>{{ item.name }}</h1>@if(item.description){<div>{{ item.description }}</div>}<small>{{ item.itemCount || item.items?.length || 0 }} {{ i18n.collectionT('productsInCollection') }}</small></header>
        <div class="collection-products">
          @for (product of item.items || []; track product.id) {
            <a [routerLink]="i18n.routePath('listing',{slug:product.slug})">
              <div class="media">@if(product.imageUrl){<app-responsive-image [url]="mediaUrl(product.imageUrl)" [mediaSizes]="product.imageSizes" [alt]="product.title" displaySizes="(max-width:700px) 44vw,280px" />}</div>
              <strong>{{ product.title }}</strong>
            </a>
          } @empty { <div class="collection-state">{{ i18n.collectionT('collectionEmpty') }}</div> }
        </div>
      } @else {
        <div class="collection-state">{{ i18n.collectionT('collectionNotFound') }}</div>
      }
    </section>
  `,
  styles: [`:host{display:block}.shared-collection{display:grid;gap:28px;margin:0 auto;max-width:1240px}.shared-collection>header{background:var(--surface);border:1px solid var(--border);border-radius:32px;padding:clamp(24px,5vw,56px)}header p,header small{color:var(--accent);font-weight:900}header h1{font-size:clamp(2.5rem,7vw,6rem);letter-spacing:-.06em;margin:8px 0}header div{color:var(--muted);font-size:1.08rem;margin-bottom:18px}.collection-products{display:grid;gap:18px;grid-template-columns:repeat(4,minmax(0,1fr))}.collection-products>a{background:var(--surface);border:1px solid var(--border);border-radius:24px;color:var(--ink);display:grid;gap:12px;padding:10px;text-decoration:none}.media{aspect-ratio:1/1;background:var(--paper);border-radius:18px;overflow:hidden}.media app-responsive-image{height:100%;width:100%}.collection-products strong{padding:4px 6px 10px}.collection-state{background:var(--surface);border:1px solid var(--border);border-radius:28px;color:var(--muted);padding:40px}@media(max-width:900px){.collection-products{grid-template-columns:repeat(2,minmax(0,1fr))}}`]
})
export class CollectionPage {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(FavoriteCollectionService);
  private readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  protected readonly collection = signal<UserCollection|null>(null);
  protected readonly loading = signal(true);

  constructor() {
    this.route.paramMap.subscribe((params) => void this.load(Number(params.get('id') || 0), params.get('slug') || ''));
  }

  protected mediaUrl(value: string): string {
    if (/^https?:\/\//i.test(value)) return value;
    return `${this.site.config().apiBaseUrl}/${value.replace(/^\/+/, '')}`;
  }

  private async load(id: number, slug: string): Promise<void> {
    this.loading.set(true);
    try { this.collection.set(this.site.enabled('collections') ? await this.service.publicCollection(id, slug) : null); }
    catch { this.collection.set(null); }
    finally { this.loading.set(false); }
  }
}
